import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const TechIntro = ({ videoUrl, titleWhite, titleGradient, ctaText = "Get Started Today" }) => {
  const videoRef = useRef(null);

  const getOptimizedVideoUrl = (url) => {
    if (!url || !url.includes('res.cloudinary.com/video/upload/')) {
      return url;
    }

    const [base, suffix] = url.split('/video/upload/');
    return `${base}/video/upload/q_auto,f_auto,w_auto,dpr_auto/${suffix}`;
  };

  const optimizedVideoUrl = getOptimizedVideoUrl(videoUrl);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.defaultMuted = true;
      videoRef.current.play().catch(err => {
        console.warn("Video autoplay failed:", err);
      });
    }
  }, []);

  return (
    <section
      className="relative min-h-[80vh] flex items-center overflow-hidden py-20 px-6"
      style={{ backgroundColor: '#001534', color: '#ffffff' }}
    >
      {/* Background Video */}
      <div className="absolute inset-0 z-[1] overflow-hidden">
        <video
          ref={videoRef}
          loop
          muted
          defaultMuted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.75 }}
        >
          <source src={optimizedVideoUrl} type="video/mp4" />
        </video>
        <div
          className="absolute inset-0 z-[2]"
          style={{ background: 'linear-gradient(135deg, rgba(0, 21, 52, 0.8) 0%, rgba(0, 21, 52, 0.5) 100%)' }}
        />
      </div>

      {/* Content - Left Aligned */}
      <div className="relative z-[3] mx-auto w-full max-w-6xl text-left flex flex-col items-start">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="flex flex-col items-start max-w-4xl"
        >

          {/* Title */}
          <h1 className="text-3xl md:text-5xl lg:text-[52px] font-bold leading-[1.3] tracking-tight mb-6">
            {/* White text in single line */}
            <span style={{ color: '#ffffff', display: 'block' }}>
              {titleWhite}
            </span>
            {/* Gradient text with gap */}
            <span
              style={{
                background: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                display: 'block',
                marginTop: '16px', // Gap between lines
              }}
            >
              {titleGradient}
            </span>
          </h1>

          {/* CTA - Rectangle Shape */}
          <Link
            to="/contact"
            onClick={() => window.scrollTo(0, 0)}
            className="inline-flex items-center gap-3 px-4 py-4 text-base font-semibold mt-4 no-underline transition-all duration-300 hover:-translate-y-0.5"
            style={{
              background: '#005FA3',
              color: '#ffffff',
              boxShadow: '0 4px 20px rgba(0, 198, 255, 0.3)',
              borderRadius: '4px', // Rectangle shape instead of rounded-full
            }}
          >
            <span>{ctaText}</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default TechIntro;
